import React from 'react';
import { AlertTriangle, Check } from 'lucide-react';

export const TableauRecapConsommations = ({ consommationsParPDL, pdls, mois, onUpdateConsommation }) => {
  // Calculer stats
  const totalCellules = pdls.length * mois.length;
  const cellulesRemplies = pdls.reduce((sum, pdl) => {
    const conso = consommationsParPDL[pdl.pdl] || {};
    return sum + Object.keys(conso).filter(m => conso[m] > 0).length;
  }, 0);
  const cellulesVides = totalCellules - cellulesRemplies;
  const tauxRemplissage = (cellulesRemplies / totalCellules * 100).toFixed(1);

  return (
    <div className="border border-[#BFC6CC] rounded-lg overflow-hidden">
      {/* Header stats */}
      <div className="bg-[#F5F6F9] px-6 py-4 border-b border-[#BFC6CC]">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#191D1E]">Vue d'ensemble Consommations</h3>
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#10D183]" />
              <span className="text-[#687175]">{cellulesRemplies} remplies</span>
            </div>
            {cellulesVides > 0 && (
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#F9C80E]" />
                <span className="text-[#F9C80E] font-medium">{cellulesVides} vides</span>
              </div>
            )}
            <div className="px-3 py-1 bg-[#397DFF] text-white rounded-full text-xs font-medium">
              {tauxRemplissage}% complet
            </div>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#F5F6F9] sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#191D1E] border-r border-[#BFC6CC] sticky left-0 bg-[#F5F6F9] z-10">
                PDL
              </th>
              {mois.map((m, idx) => (
                <th key={idx} className="px-3 py-3 text-center text-xs font-semibold text-[#191D1E] border-l border-[#BFC6CC] min-w-[80px]">
                  {m.label}
                </th>
              ))}
              <th className="px-4 py-3 text-right text-sm font-semibold text-[#191D1E] border-l-2 border-[#397DFF] sticky right-0 bg-[#F5F6F9] z-10">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {pdls.map((pdl, pdlIdx) => {
              const totalPDL = Object.values(consommationsParPDL[pdl.pdl] || {}).reduce((sum, v) => sum + v, 0);
              
              return (
                <tr key={pdlIdx} className="border-t border-[#BFC6CC] hover:bg-[#F5F6F9]">
                  <td className="px-4 py-2 font-mono text-sm text-[#191D1E] border-r border-[#BFC6CC] sticky left-0 bg-white">
                    {pdl.pdl}
                  </td>
                  {mois.map((m, mIdx) => {
                    const valeur = consommationsParPDL[pdl.pdl]?.[m.value] || 0;
                    const estVide = valeur === 0;
                    
                    return (
                      <td 
                        key={mIdx} 
                        className={`px-1 py-1 border-l border-[#BFC6CC] ${estVide ? 'bg-[#F9C80E]/10' : ''}`}
                      >
                        <input
                          type="number"
                          step="0.01"
                          value={valeur || ''}
                          onChange={(e) => onUpdateConsommation(pdl.pdl, m.value, e.target.value)}
                          className={`w-full px-2 py-1 text-center border-0 focus:ring-2 focus:ring-[#397DFF] font-mono text-xs ${
                            estVide ? 'bg-[#F9C80E]/20 font-bold' : 'bg-transparent'
                          }`}
                          placeholder="0"
                        />
                      </td>
                    );
                  })}
                  <td className="px-4 py-2 text-right font-mono text-sm font-semibold text-[#191D1E] border-l-2 border-[#397DFF] sticky right-0 bg-white">
                    {Math.round(totalPDL).toLocaleString('fr-FR')}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-[#F5F6F9] border-t-2 border-[#397DFF]">
            <tr>
              <td className="px-4 py-3 font-semibold text-[#191D1E] sticky left-0 bg-[#F5F6F9]">
                Total
              </td>
              {mois.map((m, idx) => {
                const totalMois = pdls.reduce((sum, pdl) => sum + (consommationsParPDL[pdl.pdl]?.[m.value] || 0), 0);
                return (
                  <td key={idx} className="px-3 py-3 text-center font-mono text-xs font-semibold text-[#191D1E] border-l border-[#BFC6CC]">
                    {Math.round(totalMois).toLocaleString('fr-FR')}
                  </td>
                );
              })}
              <td className="px-4 py-3 text-right font-mono text-sm font-bold text-[#397DFF] sticky right-0 bg-[#F5F6F9]">
                {Math.round(pdls.reduce((sum, pdl) => sum + Object.values(consommationsParPDL[pdl.pdl] || {}).reduce((s, v) => s + v, 0), 0)).toLocaleString('fr-FR')}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Légende */}
      <div className="px-6 py-4 bg-[#F5F6F9] border-t border-[#BFC6CC] flex items-center gap-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#F9C80E]/20 border border-[#F9C80E]"></div>
          <span className="text-[#687175]">Cellule vide (0 kWh)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-white border border-[#BFC6CC]"></div>
          <span className="text-[#687175]">Cellule remplie</span>
        </div>
      </div>
    </div>
  );
};
